import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { TEAM_SCHEMA } from "../data/seo";
import { api } from "../lib/api";
import { CONTACT, TEAM } from "../data/site";
import { ArrowUpRight, BadgeCheck, X, Loader2, Briefcase } from "lucide-react";

const WaitlistDialog = ({ clinician, onClose }) => {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name || !f.last_name || !f.email) { toast.error("Please share your name and email."); return; }
    setLoading(true);
    try {
      await api.post("/clinician/waitlist", { ...f, clinician });
      toast.success(`You're on ${clinician}'s waitlist. We'll reach out when a spot opens.`);
      onClose();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 flex items-center justify-center p-4" onClick={onClose} data-testid="waitlist-dialog">
      <div className="bg-cream max-w-lg w-full p-8 md:p-10 border border-gold/25 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-navy/60 hover:text-navy" data-testid="waitlist-close"><X size={20} /></button>
        <p className="font-script text-3xl text-gold mb-1 capitalize">{clinician}</p>
        <h3 className="font-serif text-2xl text-navy mb-4">Join the waitlist</h3>
        <p className="text-charcoal/70 mb-6 text-sm">We'll email you the moment a spot opens up.</p>
        <form onSubmit={submit} className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <input className="intake-input" placeholder="First name *" value={f.first_name} onChange={set("first_name")} data-testid="clin-wl-first" />
            <input className="intake-input" placeholder="Last name *" value={f.last_name} onChange={set("last_name")} data-testid="clin-wl-last" />
          </div>
          <input className="intake-input" placeholder="Email *" type="email" value={f.email} onChange={set("email")} data-testid="clin-wl-email" />
          <input className="intake-input" placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="clin-wl-phone" />
          <textarea className="intake-input resize-none" rows={2} placeholder="Anything we should know?" value={f.notes} onChange={set("notes")} data-testid="clin-wl-notes" />
          <button type="submit" disabled={loading} data-testid="clin-wl-submit" className="bg-navy text-cream px-8 py-4 rounded-full hover:bg-teal transition-colors inline-flex items-center gap-2 disabled:opacity-60">
            {loading ? <><Loader2 size={17} className="animate-spin" /> Joining…</> : <>Notify me first <ArrowUpRight size={17} /></>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default function Team() {
  const [wlFor, setWlFor] = useState(null);
  const [careersOpen, setCareersOpen] = useState(false);
  return (
    <div data-testid="team-page" className="pt-32">
      <SEO
        title="Meet Our Christian Counselors | Beyond Blessed"
        description="Tanya Richardson, LMHC and Adrianne, Registered Mental Health Counselor Intern — warm, faith-based therapy in Wesley Chapel, Tampa & telehealth across Florida."
        path="/team"
        jsonLd={TEAM_SCHEMA}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <Reveal>
          <p className="uppercase tracking-[0.3em] text-xs text-teal mb-4">The Team</p>
          <h1 className="font-serif text-5xl md:text-7xl text-navy leading-none">Meet your guides</h1>
          <p className="text-charcoal/70 max-w-xl mt-6">Warm, intuitive, and deeply present clinicians committed to walking with you toward healing.</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 space-y-28">
        {TEAM.map((m, i) => (
          <div key={m.name} className={`grid lg:grid-cols-2 gap-14 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`team-member-${i}`}>
            <Reveal>
              <div className="relative">
                <div className="bg-white p-3 shadow-sm rotate-[-1.5deg]">
                  <div className="w-full h-[560px] overflow-hidden">
                    <img src={m.image} alt={m.name} className="w-full h-full object-cover" style={m.name === 'Tanya Richardson' ? { transform: 'rotate(-90deg)', transformOrigin: 'center' } : {}} />
                  </div>
                </div>
                {m.imageAlt && (
                  <div className="absolute -bottom-8 right-4 bg-white p-2 shadow-md rotate-[4deg] hidden md:block w-40">
                    <img src={m.imageAlt} alt={`${m.name} — Beyond Blessed counseling`} className="w-full h-40 object-cover" />
                  </div>
                )}
                {m.accepting && (
                  <div className="absolute top-4 left-4 bg-navy text-cream text-xs uppercase tracking-widest px-4 py-2 rounded-full flex items-center gap-2">
                    <BadgeCheck size={14} className="text-goldbright" /> Accepting new clients
                  </div>
                )}
                {m.full && (
                  <div className="absolute top-4 left-4 bg-gold text-cream text-xs uppercase tracking-widest px-4 py-2 rounded-full flex items-center gap-2" data-testid="clinician-full-badge">
                    Currently Full · Waitlist Only
                  </div>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-script text-4xl text-gold mb-2">{m.name}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-navy mb-2">{m.role}</h2>
              <p className="uppercase tracking-[0.15em] text-xs text-teal mb-6">{m.credentials}</p>
              <p className="text-charcoal/75 leading-relaxed mb-8">{m.bio}</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {m.tags.map((t) => (
                  <span key={t} className="text-sm border border-navy/15 rounded-full px-4 py-1.5 text-navy/70">{t}</span>
                ))}
              </div>
              {m.full ? (
                <button type="button" onClick={() => setWlFor(m.name.split(" ")[0].toLowerCase())} data-testid={`team-waitlist-${i}`} className="inline-flex items-center gap-2 border border-navy/30 text-navy px-7 py-3.5 rounded-full hover:bg-navy hover:text-cream transition-colors">
                  Join {m.name.split(" ")[0]}'s waitlist <ArrowUpRight size={16} />
                </button>
              ) : (
                <a href={CONTACT.request} target="_blank" rel="noreferrer" data-testid={`team-book-${i}`} className="inline-flex items-center gap-2 bg-navy text-cream px-7 py-3.5 rounded-full hover:bg-teal transition-colors">
                  Book with {m.name.split(" ")[0]} <ArrowUpRight size={16} />
                </a>
              )}
            </Reveal>
          </div>
        ))}
      </section>

      <section className="bg-navy text-cream py-24" data-testid="team-hiring">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-script text-4xl text-goldbright mb-3">We are growing</p>
          <h2 className="font-serif text-4xl md:text-5xl mb-5">We're hiring compassionate clinicians</h2>
          <p className="text-cream/70 max-w-xl mx-auto mb-8">If you're a licensed or registered mental health professional who shares our heart for faith-based, trauma-informed care, we'd love to hear from you.</p>
          <button onClick={() => setCareersOpen(true)} data-testid="hiring-cta" className="bg-goldbright text-navy px-8 py-4 rounded-full inline-flex items-center gap-2 hover:bg-cream transition-colors">
            <Briefcase size={17} /> Reach out about careers
          </button>
          <p className="text-cream/50 text-xs mt-4">Or email us directly at <a href={`mailto:${CONTACT.email}?subject=Career Inquiry`} className="underline hover:text-goldbright">{CONTACT.email}</a></p>
        </div>
      </section>

      {wlFor && <WaitlistDialog clinician={wlFor} onClose={() => setWlFor(null)} />}
      {careersOpen && <CareersDialog onClose={() => setCareersOpen(false)} />}
    </div>
  );
}

const CareersDialog = ({ onClose }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "", credentials: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) { toast.error("Please share your name, email, and a short note."); return; }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: f.name, email: f.email, phone: f.phone,
        subject: `Career Inquiry — ${f.role || "General"}${f.credentials ? ` (${f.credentials})` : ""}`,
        message: `Role of interest: ${f.role || "—"}\nCredentials: ${f.credentials || "—"}\n\n${f.message}`,
      });
      toast.success("Thanks! We received your inquiry and will be in touch soon.");
      onClose();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="careers-dialog">
      <div className="bg-cream max-w-lg w-full p-8 md:p-10 border border-gold/25 relative my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-navy/60 hover:text-navy" data-testid="careers-close"><X size={20} /></button>
        <p className="font-script text-3xl text-gold mb-1">Join our team</p>
        <h3 className="font-serif text-2xl text-navy mb-4">Tell us about you</h3>
        <p className="text-charcoal/70 mb-6 text-sm">Faith-based, trauma-informed clinicians welcome. We'll respond within a few business days.</p>
        <form onSubmit={submit} className="space-y-6">
          <input className="intake-input" placeholder="Full name *" value={f.name} onChange={set("name")} data-testid="careers-name" />
          <input className="intake-input" placeholder="Email *" type="email" value={f.email} onChange={set("email")} data-testid="careers-email" />
          <input className="intake-input" placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} data-testid="careers-phone" />
          <div className="grid sm:grid-cols-2 gap-6">
            <input className="intake-input" placeholder="Role of interest (e.g. LMHC)" value={f.role} onChange={set("role")} data-testid="careers-role" />
            <input className="intake-input" placeholder="Credentials / License" value={f.credentials} onChange={set("credentials")} data-testid="careers-credentials" />
          </div>
          <textarea className="intake-input resize-none" rows={4} placeholder="Tell us a little about yourself and your practice heart… *" value={f.message} onChange={set("message")} data-testid="careers-message" />
          <button type="submit" disabled={loading} data-testid="careers-submit" className="bg-navy text-cream px-8 py-4 rounded-full hover:bg-teal transition-colors inline-flex items-center gap-2 disabled:opacity-60">
            {loading ? <><Loader2 size={17} className="animate-spin" /> Sending…</> : <>Send inquiry <ArrowUpRight size={17} /></>}
          </button>
        </form>
      </div>
    </div>
  );
};
